import { signIn } from "aws-amplify/auth";
import { AlertCircle } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { AuthError, resendSignUpCode } from "@aws-amplify/auth";
import { zodResolver } from "@hookform/resolvers/zod";

import { SignInFormData, signInSchema } from "../types/sign-in.schema";

export function SignInForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const mfaError = searchParams.get("mfaerror");

  const form = useForm<SignInFormData>({
    resolver: zodResolver(signInSchema),
    defaultValues: {
      email: "",
      password: "",
      rememberMe: false,
    },
  });

  //set form error if mfaerror is true
  useEffect(() => {
    if (mfaError) {
      form.setError("root", {
        message: "Your session has expired! Please sign in again",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mfaError]);

  async function onSubmit(values: SignInFormData) {
    try {
      const response = await signIn({
        username: values.email.toLowerCase().trim(),
        password: values.password,
      });

      // Get the redirect URL from the search params, if it exists
      const searchParams = new URLSearchParams(location.search);
      const redirectUrl = searchParams.get("redirect") || "/";

      switch (response.nextStep.signInStep) {
        case "CONTINUE_SIGN_IN_WITH_TOTP_SETUP":
          navigate("/auth/setup-2fa", {
            state: {
              secret: response.nextStep.totpSetupDetails
                .getSetupUri("ClassView")
                .toString(),
              redirectUrl: redirectUrl,
            },
          });
          break;
        case "CONFIRM_SIGN_IN_WITH_TOTP_CODE":
          navigate(`/auth/verify-totp?redirect=${redirectUrl}`, {
            state: {
              rememberMe: values.rememberMe,
            },
          });
          break;
        case "CONFIRM_SIGN_IN_WITH_SMS_CODE":
          navigate(`/auth/verify-sms?redirect=${redirectUrl}`, {
            state: {
              rememberMe: values.rememberMe,
              phoneNumber: response.nextStep.codeDeliveryDetails?.destination,
            },
          });
          break;
        case "CONFIRM_SIGN_IN_WITH_EMAIL_CODE":
          navigate(`/auth/verify-email?redirect=${redirectUrl}`, {
            state: {
              rememberMe: values.rememberMe,
              email: response.nextStep.codeDeliveryDetails?.destination,
            },
          });
          break;
        case "CONTINUE_SIGN_IN_WITH_MFA_SELECTION":
          navigate(`/auth/select-mfa?redirect=${redirectUrl}`, {
            state: {
              rememberMe: values.rememberMe,
              allowedMFATypes: response.nextStep.allowedMFATypes,
            },
          });
          break;
        case "CONFIRM_SIGN_UP":
          await resendSignUpCode({
            username: values.email.toLowerCase().trim(),
          });

          navigate(`/sign-up/confirm-email?redirect=${redirectUrl}`, {
            state: {
              email: values.email,
            },
          });
          break;
        default:
          break;
      }
      // // Navigate to the redirect URL or the default home page
      if (response.isSignedIn) {
        navigate(redirectUrl);
      }
      // redirection happens in the unauthenticated guard
    } catch (error) {
      if (error instanceof AuthError) {
        switch (error.name) {
          case "UserNotFoundException":
          case "NotAuthorizedException":
            form.setError("email", {
              type: "manual",
              message: "Invalid email or password",
            });
            form.setError("password", {
              type: "manual",
              message: "Invalid email or password",
            });
            break;
          case "UserNotConfirmedException":
            form.setError("email", {
              type: "manual",
              message: "Please verify your email address",
            });
            break;
          case "MFAMethodNotFoundException":
            form.setError("root", {
              type: "manual",
              message: error.message,
            });
            break;
          case "UserAlreadyAuthenticatedException":
            form.setError("root", {
              type: "manual",
              message: error.message,
            });
            break;
          default:
            form.setError("root", {
              type: "manual",
              message: "An unexpected error occurred. Please try again.",
            });
        }
      } else {
        console.error("Unexpected error:", error);
        form.setError("root", {
          type: "manual",
          message: "An unexpected error occurred. Please try again.",
        });
      }
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input
                  placeholder="olivia@classview.com"
                  {...field}
                  autoFocus
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Password</FormLabel>
              <FormControl>
                <Input type="password" placeholder="••••••" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-between">
          <FormField
            control={form.control}
            name="rememberMe"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="leading-none">
                  <FormLabel>Remember me</FormLabel>

                  <FormMessage />
                </div>
              </FormItem>
            )}
          />

          <Button variant="link" className="h-auto p-0 text-muted" asChild>
            <Link to="/auth/forgot-password">Forgot Password?</Link>
          </Button>
        </div>

        {form.formState.errors.root && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Something went wrong</AlertTitle>
            <AlertDescription>
              {form.formState.errors.root.message}
            </AlertDescription>
          </Alert>
        )}
        <Button
          type="submit"
          className="w-full"
          isLoading={form.formState.isSubmitting}
          isAnimated
        >
          Sign In
        </Button>
      </form>
    </Form>
  );
}
